<template>
  <div>
    <page-header class="mb-3" title="Reports"/>
    <loading-indicator v-if="pending"></loading-indicator>
    <ReportForm v-on:submit="submitReportDates" class="mb-10"/>
    <div>
      <div class="flex pt-4">
        <div class="offset-lg-1 col-lg-10 col-md-12">
          <canvas id="popularArticlesChart" ref="popularArticlesGraph"></canvas>
        </div>
        <div class="col-lg-1 hidden lg:block"></div>
      </div>
      <div class="flex pt-4">
        <div class="offset-lg-1 col-lg-10 col-md-12">
          <canvas id="totalArticlesChart" ref="totalArticlesGraph"></canvas>
        </div>
        <div class="col-lg-1 hidden lg:block"></div>
      </div>
      <div class="flex pt-4">
        <div class="offset-lg-1 col-lg-10 col-md-12">
          <canvas id="loginsChart" ref="loginsGraph"></canvas>
        </div>
        <div class="col-lg-1 hidden lg:block"></div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Chart from 'chart.js';
  import moment from 'moment';

  import PageHeader from '@/component/PageHeader';
  import ReportForm from './ReportForm';
  import { CHANGE_DOC_TITLE } from '@/store/actions/app.actions';
  import { SHOW_NOTIFICATION } from "@/store/actions/app.actions";
  import ReportService from '@/common/services/report.service';
  import AppFooter from '@/component/AppFooter';
  import LoadingIndicator from '@/component/LoadingIndicator';

  export default {
    name: 'Reports',

    components: {
      AppFooter,
      PageHeader,
      ReportForm,
      LoadingIndicator
    },

    data: () => {
      return {
        pending: false,
        originalResults: true,
        loginsGraph: null,
        articlesGraph: null,
        totalArticlesGraph: null,
      }
    },

    created: function () {
      this.$store.dispatch(CHANGE_DOC_TITLE, {title: 'Admin Reports', useBaseTitle: true});
    },

    computed: {
      ...mapGetters([
        'pages'
      ])
    },

    methods: {
      fillMissingDates(startDate, endDate, data) {
        startDate = moment(startDate);
        endDate = moment(endDate);

        const days = endDate.diff(startDate, 'd', false);
        let newData = [];
        let val;

        for (let i = 0; i <= days; i++) {
          val = 0;

          for (let j = 0; j < data.length; j++){
            if (data[j].x === startDate.format('YYYY-MM-DD')) {
              val = data[j].y;
            }
          }

          newData[i] = {
            x: startDate.format('YYYY-MM-DD'),
            y: val
          };

          startDate.add(1, 'd');
        }

        return newData;
      },
      submitReportDates(reportDates) {
        this.pending = true;

        ReportService.getReportData(reportDates)
          .then((response) => {
            const loginsByDate = this.fillMissingDates(response.startDate, response.endDate, response.loginsByDate);
            const popularArticles = response.popularArticles;
            const totalArticles = this.fillMissingDates(response.startDate, response.endDate, response.totalArticles);

            // If graphs have already been created, destroy them so that old information will not
            // display on hover.
            if(this.originalResults === true){
              this.originalResults = false;
            } else {
              this.loginsGraph.destroy();
              this.articlesGraph.destroy();
              this.totalArticlesGraph.destroy();
            }

            this.createLoginsGraph(this.$refs.loginsGraph, loginsByDate);
            this.createBiosGraph(this.$refs.popularArticlesGraph, popularArticles);
            this.createTotalBiosGraph(this.$refs.totalArticlesGraph, totalArticles);
          })
          .catch(e => this.$store.dispatch(SHOW_NOTIFICATION, {type: 'error', message: 'There was a problem generating the reports.'}))
          .finally(() => this.pending = false);
      },
      createLoginsGraph: function ($el, loginDates) {
        this.loginsGraph = new Chart($el, {
          type: 'line',
          data: {
            datasets: [{
              data: loginDates,
              backgroundColor: '#ed562f',
              borderColor: '#2d4c90',
              borderWidth: 1,
              fill: false,
              lineTension: 0,
            }]
          },
          options: {
            scales: {
              xAxes: [{
                type: 'time',
                time: {
                  format: 'YYYY-MM-DD',
                  unit: 'day'
                },
                ticks: {
                  fontSize: 16,
                },
                scaleLabel: {
                  display: true,
                  labelString: "Date",
                  fontSize: 20,
                }
              }],
              yAxes: [{
                type: 'linear',
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                  fontSize: 16,
                },
                display: true,
                min: 0,
                scaleLabel: {
                  display: true,
                  labelString: "Times Logged In",
                  fontSize: 20,
                }
              }]
            },
            title: {
              display: true,
              position: 'top',
              padding: 15,
              text: 'Logins by Date',
              fontSize: 30,
            },
            legend: {
              display: false,
            }
          }
        });
      },
      createBiosGraph: function ($el, bios) {
        this.articlesGraph = new Chart($el, {
          type: 'bar',
          data: {
            labels: Object.keys(bios),
            datasets: [{
              data: Object.values(bios),
              backgroundColor: '#ed562f',
              borderColor: '#2d4c90',
              borderWidth: 1
            }]
          },
          options: {
            scales: {
              xAxes: [{
                type: 'category',
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: "Article",
                  fontSize: 20,
                },
                ticks: {
                  fontSize: 16,
                },
              }],
              yAxes: [{
                type: 'linear',
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                  fontSize: 16,
                },
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: "Times Viewed",
                  fontSize: 20,
                }
              }]
            },
            title: {
              display: true,
              position: 'top',
              padding: 15,
              text: 'Top Ten Articles Viewed',
              fontSize: 30,
            },
            legend: {
              display: false,
            }
          }
        });
      },
      createTotalBiosGraph: function ($el, totalBios) {
        this.totalArticlesGraph = new Chart($el, {
          type: 'line',
          data: {
            datasets: [{
              data: totalBios,
              backgroundColor: '#ed562f',
              borderColor: '#2d4c90',
              borderWidth: 1,
              fill: false,
              lineTension: 0,
            }]
          },
          options: {
            scales: {
              xAxes: [{
                type: 'time',
                time: {
                  format: 'YYYY-MM-DD',
                  unit: 'day'
                },
                ticks: {
                  fontSize: 16,
                },
                scaleLabel: {
                  display: true,
                  labelString: "Date",
                  fontSize: 20,
                },
              }],
              yAxes: [{
                type: 'linear',
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                  fontSize: 16,
                },
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: "Articles Viewed",
                  fontSize: 20,
                }
              }]
            },
            title: {
              display: true,
              position: 'top',
              padding: 15,
              text: 'Articles Viewed by Date',
              fontSize: 30,
            },
            legend: {
              display: false,
            }
          }
        });
      }
    },
  };
</script>
